// Whether the PBX is doing what Verso thinks it is. DRIFT, NOT MANAGEMENT.
//
// The auto attendant's controls used to live in here. They are rows of RouteTypeCard now —
// This card answers a question about US — "is our record of this number's route right" — and that is
// why the controls did not belong: an operator managing a customer's menu had to read a warning
// about our own record-keeping to find them. See RoutingTab.jsx for how the tab is laid out.
//
// Where calls to this number actually land, read from the PBX.
//
// Kevin, 2026-08-15: "add to the routing tab, options for queues and IVR ... read
// only support for lines that already are using those as routes."
//
// READ-ONLY BY DESIGN, not by omission. Verso can build an extension or a ring
// group. A queue, an IVR, a time condition or an announcement is a call flow
// somebody assembled in FreePBX, and provisioning already refuses to overwrite one
// (`classifyDestination` → 'complex'). Showing it without an edit button is the
// honest shape: an operator can see what the number does, and no control on this
// page can destroy it.
//
// WHY IT IS LIVE RATHER THAN `line.routeType`. That column records what Verso
// BUILT. A number pointed at an IVR by hand still reads `extension` there — which
// is exactly the case this card exists for, so reading Verso's own record would
// show the wrong answer precisely when it matters.

import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

// The kinds Verso builds and the rest of the tab already explains. Showing this
// card for them would be a second, quieter answer to a question RouteTypeCard
// already answers loudly.
const VERSO_BUILT = new Set(['extension', 'ring-group']);

// Kinds with a detail renderer below. Everything else still gets the card — the
// kind, the label and any `detail_error` — because knowing a number is on a time
// condition is useful even when the host cannot describe it.
const HAS_DETAIL = new Set(['ivr', 'queue']);

// The words the chooser uses, so one thing has one name across the page. `ivr` and
// `ring_group` are our column values; an operator says "auto attendant".
const KIND_WORDS = {
  extension: 'Extension',
  ring_group: 'Ring Group',
  'ring-group': 'Ring Group',
  queue: 'Queue',
  ivr: 'Auto Attendant',
};

function Row({ label, children }) {
  return (
    <div className="flex gap-3 py-1 text-sm">
      <span className="w-40 shrink-0 text-gray-500">{label}</span>
      <span className="text-gray-900">{children}</span>
    </div>
  );
}

// IvrPrompts and IvrDetail LIVED HERE and are rows of RouteTypeCard now (via AttendantFlow), when the
// auto attendant got its own card. Deleted rather than kept "in case", because two renderers for one
// menu is how the tab came to show every prompt twice for the length of one deploy.

/** A queue's members and how it rings them. */
function QueueDetail({ object }) {
  const members = object?.members || object?.agents || [];
  return (
    <>
      {object?.descr && <Row label="Description">{object.descr}</Row>}
      {(object?.strategy) && <Row label="Rings">{object.strategy}</Row>}
      {members.length > 0 ? (
        <Row label="Members">{members.map((m) => m.extension ?? m).join(', ')}</Row>
      ) : (
        <p className="mt-2 text-xs text-gray-500">The PBX did not return this queue&rsquo;s members.</p>
      )}
    </>
  );
}

export default function LiveRouteCard({ line }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['line', String(line.id), 'route-target'],
    queryFn: () => api.get(`/lines/${line.id}/route-target`),
    retry: false,
  });


  if (isLoading) return null;

  // COULD NOT LOOK ≠ NOT ROUTED. An unreachable host must never render as "this
  // number goes nowhere" — that reads as a fault in the customer's config rather
  // than in our ability to see it.
  if (isError || data?.verified === 'unavailable') {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold text-gray-900">Where calls actually go</div>
        <p className="mt-1 text-sm text-gray-500">
          Could not read this number&rsquo;s route from the PBX, so what is below may be out of date.
          {data?.reason ? ` ${data.reason}` : ''}
        </p>
      </div>
    );
  }

  // Verso built it and the rest of the tab already says so.
  if (VERSO_BUILT.has(data?.kind)) return null;

  // AND AN AUTO ATTENDANT HAS ITS OWN CARD NOW, so rendering here would say everything twice.
  //
  // That is not hypothetical tidiness: with both cards live the tab showed the menu's name,
  // description, timeout, keys and prompts, and then showed all five again immediately below. The
  // reorganisation meant to make this tab less overwhelming had briefly doubled it.
  //
  // RouteTypeCard carries what was unique to this card — the "built on the PBX" caveat, the raw
  // destination, and the drift notice — so nothing is lost by standing down. Every other flow kind
  // still renders here, because none of them has a card of its own yet.
  if (data?.kind === 'ivr') return null;

  const isFlow = ['queue', 'ivr', 'time-condition', 'announcement', 'unknown'].includes(data?.kind);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">Where calls actually go</div>
          <div className="mt-0.5 text-sm text-gray-800">
            {KIND_WORDS[data?.kind] || data?.label}
            {data?.object?.name || data?.object?.description
              ? <> — <span className="font-medium">{data.object.name || data.object.description}</span></>
              : null}
          </div>
        </div>
        <span className="inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
          read-only
        </span>
      </div>

      {isFlow && (
        <p className="mt-2 text-xs text-amber-900">
          This call flow was built on the PBX. Verso can point this number at it, but does
          not change the flow itself — its greeting, options and hours are edited on the PBX.
        </p>
      )}

      <div className="mt-3 border-t border-amber-200 pt-3">
        {data?.kind === 'queue' && <QueueDetail object={data.object} />}
        {data?.detail_error && (
          <p className="mt-1 text-xs text-amber-900">{data.detail_error}</p>
        )}
        {/* A kind with no renderer and nothing to explain would otherwise show only a
            dialplan string. Say plainly that the detail is not available rather than
            leaving a gap that reads as "nothing configured". */}
        {!HAS_DETAIL.has(data?.kind) && !data?.detail_error && (
          <p className="text-xs text-gray-600">
            Verso does not read the detail of this kind of call flow — open it on the PBX to see it.
          </p>
        )}
        {/* The raw destination, quietly, for whoever has to go and look at FreePBX. */}
        {/* Labelled, not bare. Unlabelled it reads as noise; it is here for whoever
            has to go and find this in FreePBX. */}
        {data?.destination && (
          <p className="mt-2 text-[11px] text-gray-500">
            On the PBX: <span className="font-mono">{data.destination}</span>
          </p>
        )}
      </div>

      {/* ONLY WHEN THEY ACTUALLY DISAGREE. This fired for every flow kind, which was
          right while Verso could not record one — and became false the moment it
          could: the Bud Depot line read "Verso has this recorded as ivr … not what
          the PBX is doing today" while the PBX was running exactly that IVR. A
          disagreement notice that cries wolf is worse than none. */}
      {data?.verso_route_type && isFlow && data.verso_route_type !== data.kind && (
        <p className="mt-2 text-xs text-gray-600">
          Verso has this number recorded as <span className="font-medium">{KIND_WORDS[data.verso_route_type] || data.verso_route_type}</span>,
          which is what it would build — not what the PBX is doing today.
        </p>
      )}
    </div>
  );
}
